import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowRight, Users } from "lucide-react";
import { openPreRegister } from "@/lib/preRegisterModal";
import { useFoundingDriverCount } from "@/hooks/useFoundingDriverCount";
import { HeroAudienceToggle } from "@/components/sections/HeroAudienceToggle";
import { HeroFloatingJobCard } from "@/components/sections/HeroFloatingJobCard";
import { HeroTrustBar } from "@/components/sections/HeroTrustBar";

const AUDIENCE_CONTENT = {
  drivers: {
    tagline: "DRIVERS • OWNER-OPERATORS • RELIEF LOADS",
    headline: ["KEEP THE", "TRIP MOVING."],
    subheadline:
      "Whether you're a company driver or run your own truck, RelayRigs matches you with relief loads, backhauls, and RV transport opportunities — so your wheels never sit idle. We're building the largest driver network in the industry, giving brokers a wide range of vetted drivers at their fingertips.",
    primary: { label: "Pre-Register for RelayRigs", segment: "driver" },
    secondary: { label: "I Need a Driver", segment: "need_driver" },
  },
  carriers: {
    tagline: "CARRIERS • FLEETS • BACKUP DRIVERS",
    headline: ["NEVER MISS", "A DELIVERY."],
    subheadline:
      "Running a fleet? RelayRigs connects you with vetted relief drivers and owner-operators on demand — so a sick day, a breakdown, or a missed dispatch never turns into a missed delivery. We're building the largest driver network in the industry, giving brokers a wide range of vetted drivers at their fingertips.",
    primary: { label: "Find a Relief Driver", segment: "need_driver" },
    secondary: { label: "I'm a Driver", segment: "driver" },
  },
};

const LineReveal = ({ line, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {line}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const foundingDriverCount = useFoundingDriverCount();
  const [audience, setAudience] = useState("drivers");
  const content = AUDIENCE_CONTENT[audience];

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-[92px] overflow-hidden bg-[linear-gradient(135deg,#071B30_0%,#0E2A47_42%,#B5511A_82%,#F2761E_100%)]"
    >
      <div className="grain-overlay" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-rr-orange/25 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center pt-16 pb-24 lg:pb-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            data-testid="hero-coming-soon-badge"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-rr-orangeLight bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-rr-orangeLight animate-pulse" /> RelayRigs Is Coming
          </motion.div>

          <HeroAudienceToggle audience={audience} onChange={setAudience} />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xs font-bold tracking-[0.24em] uppercase text-white/70 mb-6"
          >
            {content.tagline}
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-heading text-5xl sm:text-6xl lg:text-[5.2rem] font-black uppercase tracking-tight leading-[0.95] text-white"
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={audience}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="block"
              >
                <LineReveal line={content.headline[0]} delay={0.05} />
                <LineReveal line={content.headline[1]} delay={0.15} />
              </motion.span>
            </AnimatePresence>
          </h1>

          <motion.p
            key={audience}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-7 text-lg text-white/80 max-w-lg leading-relaxed"
          >
            {content.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openPreRegister(content.primary.segment)}
              data-testid="hero-find-opportunities-cta"
              className="inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-4 rounded-full hover:bg-white hover:text-rr-navy transition-all hover:-translate-y-1 shadow-xl shadow-black/20"
            >
              {content.primary.label} <ArrowRight size={18} />
            </button>
            <button
              onClick={() => openPreRegister(content.secondary.segment)}
              data-testid="hero-find-driver-cta"
              className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-bold px-7 py-4 rounded-full hover:bg-white/20 transition-all hover:-translate-y-1"
            >
              {content.secondary.label}
            </button>
            <a href="#rv" data-testid="hero-rv-cta" className="inline-flex items-center gap-1 text-white font-bold underline underline-offset-4 decoration-white/40 hover:decoration-white px-2">
              RV Rescue & Delivery →
            </a>
          </motion.div>

          {foundingDriverCount !== null && foundingDriverCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              data-testid="hero-founding-driver-counter"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white/85"
            >
              <Users size={16} className="text-rr-orangeLight" />
              <span className="text-white font-extrabold">{foundingDriverCount.toLocaleString()}</span> people already on the founding waitlist
            </motion.div>
          )}

          <HeroTrustBar />
        </div>

        <motion.div
          style={{ opacity: fade }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-[2rem] overflow-hidden border-4 border-white/10 shadow-2xl shadow-black/40 aspect-[4/5]">
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1788370538342-b28c755cf61b?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="Commercial truck on the highway at night"
              className="w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-rr-navyDeep/80 via-transparent to-rr-navyDeep/20" />
          </div>

          <HeroFloatingJobCard />
        </motion.div>
      </div>
    </section>
  );
};
